import { FC, useMemo } from 'react'

import styled from 'styled-components'

import { Checkbox } from '@style-port/roov-ui-components-react'
import { TableCell, Txt } from 'src/components/atoms'
import { TableSortableTxt, TableSortType } from 'src/components/molecules'

import { useAuthority } from 'src/hooks'
import { InternalUserEntity, SelectableInternalUserEntity } from 'src/types'

const Container = styled.div`
  box-shadow: 0 5px 20px rgba(122, 137, 160, 0.1);
`

export type InternalUserSortItemType = keyof Pick<
  InternalUserEntity,
  | 'id'
  | 'name'
  | 'email'
  | 'keywords'
  | 'customRoles'
  | 'role'
  | 'createdAt'
  | 'projectCount'
  | 'isResetPassword'
>

type Props = {
  internalUsers: SelectableInternalUserEntity[]
  sortItem: InternalUserSortItemType
  sortType: TableSortType
  onSelectItem: (newItem: InternalUserSortItemType) => void
  onClickAllSelect: () => void
  className?: string
}

export const InternalUserListTableHead: FC<Props> = ({
  internalUsers,
  sortItem,
  sortType,
  onSelectItem,
  onClickAllSelect,
  className,
}) => {
  const { canAssignProject } = useAuthority()
  const isAllSelected = useMemo(() => internalUsers.every(user => user.isSelected), [internalUsers])
  const isSomeSelected = useMemo(() => internalUsers.some(user => user.isSelected), [internalUsers])

  const selectedUserCount = useMemo(
    () => internalUsers.filter(user => user.isSelected).length,
    [internalUsers]
  )

  return (
    <div className={className}>
      <Container className="tw-bg-surface-secondary tw-py-s-1">
        <div className="tw-flex tw-flex-nowrap tw-items-center">
          {canAssignProject && (
            <TableCell width={5}>
              <div className="tw-flex tw-flex-nowrap tw-items-center tw-ml-s0">
                <div role="presentation" onClick={onClickAllSelect}>
                  <Checkbox
                    nonce={undefined}
                    checked={isSomeSelected}
                    indeterminate={!isAllSelected && isSomeSelected}
                  />
                </div>
                <Txt sizeType="l" className="tw-font-bold tw-text-roovPrimary-default tw-ml-s-2">
                  {selectedUserCount}
                </Txt>
              </div>
            </TableCell>
          )}
          <TableCell width={6} align="right" className="tw-mr-s1">
            <TableSortableTxt
              text="ID"
              isActive={sortItem === 'id'}
              sortType={sortType}
              onSelect={() => onSelectItem('id')}
            />
          </TableCell>
          <TableCell width={15}>
            <TableSortableTxt
              text="お名前"
              isActive={sortItem === 'name'}
              sortType={sortType}
              onSelect={() => onSelectItem('name')}
            />
          </TableCell>
          <TableCell width={14}>
            <TableSortableTxt
              text="メールアドレス"
              isActive={sortItem === 'email'}
              sortType={sortType}
              onSelect={() => onSelectItem('email')}
            />
          </TableCell>
          <TableCell width={6} className="tw-mr-s1">
            <TableSortableTxt
              text="キーワード"
              isActive={sortItem === 'keywords'}
              sortType={sortType}
              onSelect={() => onSelectItem('keywords')}
            />
          </TableCell>
          <TableCell width={6} className="tw-mr-s1">
            <TableSortableTxt
              text="カスタムロール"
              isActive={sortItem === 'customRoles'}
              sortType={sortType}
              onSelect={() => onSelectItem('customRoles')}
            />
          </TableCell>
          <TableCell width={9} className="tw-mr-s1">
            <TableSortableTxt
              text="WORKSでの役割"
              isActive={sortItem === 'role'}
              sortType={sortType}
              onSelect={() => onSelectItem('role')}
            />
          </TableCell>
          <TableCell width={6} className="tw-mr-s1">
            <TableSortableTxt
              text="作成日"
              isActive={sortItem === 'createdAt'}
              sortType={sortType}
              onSelect={() => onSelectItem('createdAt')}
            />
          </TableCell>
          <TableCell width={6} align="right" className="tw-mr-s1">
            <TableSortableTxt
              text="プロジェクト数"
              isActive={sortItem === 'projectCount'}
              sortType={sortType}
              onSelect={() => onSelectItem('projectCount')}
            />
          </TableCell>
          <TableCell>
            <TableSortableTxt
              text="本パスワード"
              className="tw-ml-s3"
              isActive={sortItem === 'isResetPassword'}
              sortType={sortType}
              onSelect={() => onSelectItem('isResetPassword')}
            />
          </TableCell>
        </div>
      </Container>
    </div>
  )
}
